import Dexie from 'dexie';
import { db } from '@/database/db';
import { syncService } from './sync.service';
import ApiService from './ApiService';

// ============================================
// Service pour répliquer les réponses API en mode offline
// ============================================

interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  errors?: any;
}

/**
 * Mappe les endpoints API aux tables IndexedDB et formate les réponses
 * exactement comme les routes backend les renvoient
 */
class OfflineApiService {
  // Mapping des endpoints aux tables IndexedDB
  private endpointMapping: Record<string, {
    table: Dexie.Table<any, number>;
    transform?: (item: any) => any;
    listTransform?: (items: any[]) => any;
  }> = {};

  constructor() {
    this.initializeMappings();
  }

  private initializeMappings() {
    // ============================================
    // CONTRATS
    // ============================================
    this.endpointMapping['/api/contrats/liste'] = {
      table: db.contrats,
      transform: (item) => this.transformContrat(item),
      listTransform: (items) => ({
        data: items.map(item => this.transformContrat(item)),
        total: items.length
      })
    };

    this.endpointMapping['/api/contrats'] = {
      table: db.contrats,
      transform: (item) => this.transformContrat(item)
    };

    this.endpointMapping['/api/listeContrat'] = {
      table: db.contrats,
      transform: (item) => this.transformContrat(item),
      listTransform: (items) => ({
        data: items.map(item => this.transformContrat(item)),
        total: items.length
      })
    };

    // ============================================
    // PAIEMENTS (paiement_extension)
    // ============================================
    this.endpointMapping['/api/listePaiement'] = {
      table: db.paiements,
      transform: (item) => this.transformPaiement(item),
      listTransform: (items) => ({
        data: items.map(item => this.transformPaiement(item)),
        total: items.length
      })
    };

    // ============================================
    // PAIEMENTS RECETTE
    // ============================================
    this.endpointMapping['/api/listePaiement/autre/recette'] = {
      table: db.paiementsRecette,
      transform: (item) => this.transformPaiementRecette(item),
      listTransform: (items) => ({
        data: items.map(item => this.transformPaiementRecette(item)),
        total: items.length
      })
    };

    // ============================================
    // ARRIÈRES ELECTRONIC
    // ============================================
    this.endpointMapping['/api/contrat/new/arrieres'] = {
      table: db.arrieresElectronic,
      transform: (item) => this.transformArriereElectronic(item),
      listTransform: (items) => items.map(item => this.transformArriereElectronic(item))
    };

    this.endpointMapping['/api/contrat/getArrieresByContrat'] = {
      table: db.arrieresElectronic,
      transform: (item) => this.transformArriereElectronic(item),
      listTransform: (items) => items.map(item => this.transformArriereElectronic(item))
    };

    // ============================================
    // USAGERS
    // ============================================
    this.endpointMapping['/api/listeUsagers'] = {
      table: db.usagers,
      transform: (item) => this.transformUsager(item),
      listTransform: (items) => {
        // Format exact comme getAllUsagers retourne
        return {
          data: items.map(item => this.transformUsager(item)),
          totalPages: Math.ceil(items.length / 10),
          totalElements: items.length,
          limit: 10
        };
      }
    };

    this.endpointMapping['/api/usagers'] = {
      table: db.usagers,
      transform: (item) => this.transformUsager(item)
    };

    this.endpointMapping['/api/getUsagerByKey'] = {
      table: db.usagers,
      transform: (item) => this.transformUsager(item),
      listTransform: (items) => ({
        data: items.map(item => this.transformUsager(item))
      })
    };

    // ============================================
    // EMPLACEMENTS
    // ============================================
    this.endpointMapping['/api/listeEmplacement'] = {
      table: db.emplacements,
      transform: (item) => this.transformEmplacement(item),
      listTransform: (items) => ({
        data: items.map(item => this.transformEmplacement(item)),
        total: items.length
      })
    };

    this.endpointMapping['/api/emplacement'] = {
      table: db.emplacements,
      transform: (item) => this.transformEmplacement(item)
    };

    // ============================================
    // USERS
    // ============================================
    this.endpointMapping['/api/liste/users'] = {
      table: db.users,
      transform: (item) => this.transformUser(item),
      listTransform: (items) => ({
        data: items.map(item => this.transformUser(item)),
        total: items.length
      })
    };

    this.endpointMapping['/api/users'] = {
      table: db.users,
      transform: (item) => this.transformUser(item)
    };

    this.endpointMapping['/api/tout/users'] = {
      table: db.users,
      transform: (item) => this.transformUser(item),
      listTransform: (items) => items.map(item => this.transformUser(item))
    };

    // ============================================
    // ROLES
    // ============================================
    this.endpointMapping['/api/roles'] = {
      table: db.roles,
      transform: (item) => this.transformRole(item),
      listTransform: (items) => ({
        data: items.map(item => this.transformRole(item)),
        total: items.length
      })
    };

    // ============================================
    // PERMISSIONS
    // ============================================
    this.endpointMapping['/api/permissions'] = {
      table: db.permissions,
      transform: (item) => this.transformPermission(item),
      listTransform: (items) => ({
        data: items.map(item => this.transformPermission(item)),
        total: items.length
      })
    };

    this.endpointMapping['/api/all/permissions'] = {
      table: db.permissions,
      transform: (item) => this.transformPermission(item),
      listTransform: (items) => items.map(item => this.transformPermission(item))
    };

    // ============================================
    // ROLE PERMISSIONS
    // ============================================
    this.endpointMapping['/api/roles/permissions'] = {
      table: db.rolePermissions,
      transform: (item) => this.transformRolePermission(item),
      listTransform: (items) => ({
        data: items.map(item => this.transformRolePermission(item)),
        total: items.length
      })
    };

    // ============================================
    // MODE PAIEMENT
    // ============================================
    this.endpointMapping['/api/listeMode'] = {
      table: db.modePaiements,
      transform: (item) => this.transformModePaiement(item),
      listTransform: (items) => items.map(item => this.transformModePaiement(item))
    };

    // ============================================
    // SECTEURS
    // ============================================
    this.endpointMapping['/api/liste/secteur'] = {
      table: db.secteurs,
      transform: (item) => this.transformSecteur(item),
      listTransform: (items) => items.map(item => this.transformSecteur(item))
    };

    this.endpointMapping['/api/all/secteur'] = {
      table: db.secteurs,
      transform: (item) => this.transformSecteur(item),
      listTransform: (items) => items.map(item => this.transformSecteur(item))
    };

    this.endpointMapping['/api/secteur'] = {
      table: db.secteurs,
      transform: (item) => this.transformSecteur(item),
      listTransform: (items) => items.map(item => this.transformSecteur(item))
    };

    // ============================================
    // ZONES
    // ============================================
    this.endpointMapping['/api/zones'] = {
      table: db.zones,
      transform: (item) => this.transformZone(item),
      listTransform: (items) => ({
        data: items.map(item => this.transformZone(item)),
        total: items.length
      })
    };

    // ============================================
    // MARCHES
    // ============================================
    this.endpointMapping['/api/marches'] = {
      table: db.marches,
      transform: (item) => this.transformMarche(item),
      listTransform: (items) => items.map(item => this.transformMarche(item))
    };

    // ============================================
    // ACTIVITES
    // ============================================
    this.endpointMapping['/api/listeActivite'] = {
      table: db.activites,
      transform: (item) => this.transformActivite(item),
      listTransform: (items) => items.map(item => this.transformActivite(item))
    };
  }

  /**
   * Récupère les données depuis IndexedDB en formatant comme l'API
   */
  async get(endpoint: string, params?: any): Promise<ApiResponse> {
    if (syncService.isOffline()) {
      return await this.getFromIndexedDB(endpoint, params);
    }

    try {
      // Essayer l'API d'abord
      const response = await ApiService.get(endpoint, params || '');
      if (response.data) {
        // Sauvegarder dans IndexedDB
        await this.saveToIndexedDB(endpoint, response.data);
        return response.data;
      }
      throw new Error('Réponse API invalide');
    } catch (error: any) {
      // Fallback vers IndexedDB
      console.warn('API error, using IndexedDB:', error);
      return await this.getFromIndexedDB(endpoint, params);
    }
  }

  /**
   * POST avec fallback IndexedDB
   */
  async post(endpoint: string, data: any): Promise<ApiResponse> {
    if (syncService.isOffline()) {
      return await this.postToIndexedDB(endpoint, data);
    }

    try {
      const response = await ApiService.post(endpoint, data);
      if (response.data && response.data.success) {
        // Sauvegarder dans IndexedDB
        await this.saveToIndexedDB(endpoint, response.data);
      }
      return response.data;
    } catch (error: any) {
      console.warn('API error, saving to IndexedDB:', error);
      return await this.postToIndexedDB(endpoint, data);
    }
  }

  /**
   * Récupère depuis IndexedDB et formate comme l'API
   */
  private async getFromIndexedDB(endpoint: string, params?: any): Promise<ApiResponse> {
    const mapping = this.findMapping(endpoint);
    if (!mapping) {
      return {
        success: false,
        message: `Endpoint ${endpoint} non mappé pour le mode offline`
      };
    }

    try {
      let items: any[] = [];

      // Récupérer tous les éléments non supprimés
      items = await mapping.table
        .filter(item => !item.deletedAt && item.syncStatus !== 'deleted')
        .toArray();

      // Appliquer les filtres si présents dans params
      if (params && typeof params === 'object') {
        items = this.applyFilters(items, params);
      }

      // Transformer les données
      if (mapping.listTransform) {
        return {
          success: true,
          data: mapping.listTransform(items)
        };
      } else if (mapping.transform) {
        return {
          success: true,
          data: items.length > 0 ? mapping.transform(items[0]) : null
        };
      }

      return {
        success: true,
        data: items
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.message || 'Erreur lors de la récupération depuis IndexedDB'
      };
    }
  }

  /**
   * Sauvegarde dans IndexedDB (POST)
   */
  private async postToIndexedDB(endpoint: string, data: any): Promise<ApiResponse> {
    const mapping = this.findMapping(endpoint);
    if (!mapping) {
      return {
        success: false,
        message: `Endpoint ${endpoint} non mappé pour le mode offline`
      };
    }

    try {
      const localId = db.generateLocalId();
      const now = new Date();

      const entity = {
        ...data,
        localId,
        syncStatus: 'pending' as const,
        createdAt: now,
        updatedAt: now,
        version: 1
      };

      const id = await mapping.table.add(entity);
      const saved = await mapping.table.get(id);

      // Ajouter à la queue de synchronisation
      await db.syncQueue.add({
        entityType: this.getEntityTypeFromEndpoint(endpoint),
        entityId: id,
        localId,
        action: 'create',
        data: saved,
        retryCount: 0,
        createdAt: new Date()
      });

      return {
        success: true,
        data: mapping.transform ? mapping.transform(saved) : saved
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.message || 'Erreur lors de la sauvegarde dans IndexedDB'
      };
    }
  }

  /**
   * Sauvegarde les données reçues de l'API dans IndexedDB
   */
  private async saveToIndexedDB(endpoint: string, apiResponse: ApiResponse): Promise<void> {
    const mapping = this.findMapping(endpoint);
    if (!mapping) return;

    try {
      let dataToSave: any[] = [];

      if (apiResponse.data) {
        if (Array.isArray(apiResponse.data)) {
          dataToSave = apiResponse.data;
        } else if (apiResponse.data.data && Array.isArray(apiResponse.data.data)) {
          dataToSave = apiResponse.data.data;
        } else if (typeof apiResponse.data === 'object') {
          dataToSave = [apiResponse.data];
        }
      }

      if (dataToSave.length > 0) {
        const entities = dataToSave.map(item => ({
          ...item,
          syncStatus: 'synced' as const,
          lastSync: new Date(),
          createdAt: item.createdAt ? new Date(item.createdAt) : new Date(),
          updatedAt: item.updatedAt ? new Date(item.updatedAt) : new Date(),
          version: item.version || 1
        }));

        await mapping.table.bulkPut(entities);
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde dans IndexedDB:', error);
    }
  }

  /**
   * Trouve le mapping pour un endpoint
   */
  private findMapping(endpoint: string) {
    // Chercher une correspondance exacte
    if (this.endpointMapping[endpoint]) {
      return this.endpointMapping[endpoint];
    }

    // Chercher une correspondance partielle
    for (const [key, value] of Object.entries(this.endpointMapping)) {
      if (endpoint.includes(key) || key.includes(endpoint)) {
        return value;
      }
    }

    return null;
  }

  /**
   * Applique les filtres aux données
   */
  private applyFilters(items: any[], filters: any): any[] {
    let filtered = [...items];

    // Filtre par recherche textuelle (motCle, recherche, searchTerm)
    const searchTerm = filters.motCle || filters.recherche || filters.searchTerm;
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(item => {
        const searchableFields = [
          item.nom, item.prenom, item.nomComplet, item.matricule, item.tel, item.telephone,
          item.email, item.codeemp, item.ncode_emp, item.nref_emp, item.ref_emp,
          item.nomLocataire, item.nomExploitant, item.libelle_secteur, item.libelle_zone,
          item.nomMarche, item.raison_sociale, item.sigle, item.libelle, item.code
        ].filter(Boolean).join(' ').toLowerCase();
        return searchableFields.includes(search) || JSON.stringify(item).toLowerCase().includes(search);
      });
    }

    // Filtre par état
    if (filters.etat !== undefined && Array.isArray(filters.etat) && filters.etat.length > 0) {
      filtered = filtered.filter(item => {
        const itemEtat = item.etat || item.estActif || item.etape_validation;
        return filters.etat.includes(itemEtat);
      });
    }

    // Filtre par date début
    if (filters.dateDebut) {
      filtered = filtered.filter(item => {
        const itemDate = item.datePaiement || item.date_creation || item.createdAt;
        return itemDate && new Date(itemDate) >= new Date(filters.dateDebut);
      });
    }

    // Filtre par date fin
    if (filters.dateFin) {
      filtered = filtered.filter(item => {
        const itemDate = item.datePaiement || item.date_creation || item.createdAt;
        return itemDate && new Date(itemDate) <= new Date(filters.dateFin);
      });
    }

    // Filtre par secteur
    if (filters.secteur) {
      filtered = filtered.filter(item => {
        const secteurId = item.secteur?.id || item.secteur || item.code_zone;
        return secteurId === filters.secteur || secteurId === filters.secteur.id;
      });
    }

    // Filtre par zone
    if (filters.zone) {
      filtered = filtered.filter(item => {
        const zoneId = item.zone?.id || item.zone || item.marche_id;
        return zoneId === filters.zone || zoneId === filters.zone.id;
      });
    }

    // Filtre par marche
    if (filters.marche) {
      filtered = filtered.filter(item => {
        const marcheId = item.marche?.id || item.marche;
        return marcheId === filters.marche || marcheId === filters.marche.id;
      });
    }

    // Pagination
    if (filters.page && filters.limit) {
      const start = (filters.page - 1) * filters.limit;
      const end = start + filters.limit;
      filtered = filtered.slice(start, end);
    }

    return filtered;
  }

  /**
   * Détermine le type d'entité depuis l'endpoint
   */
  private getEntityTypeFromEndpoint(endpoint: string): string {
    if (endpoint.includes('contrat')) return 'contrat';
    if (endpoint.includes('paiement') && endpoint.includes('recette')) return 'paiementRecette';
    if (endpoint.includes('paiement')) return 'paiement';
    if (endpoint.includes('arriere')) return 'arriereElectronic';
    if (endpoint.includes('usager')) return 'usager';
    if (endpoint.includes('emplacement')) return 'emplacement';
    if (endpoint.includes('user')) return 'user';
    if (endpoint.includes('role') && endpoint.includes('permission')) return 'rolePermission';
    if (endpoint.includes('role')) return 'role';
    if (endpoint.includes('permission')) return 'permission';
    if (endpoint.includes('mode')) return 'modePaiement';
    if (endpoint.includes('secteur')) return 'secteur';
    if (endpoint.includes('zone')) return 'zone';
    if (endpoint.includes('marche')) return 'marche';
    if (endpoint.includes('activite')) return 'activite';
    return 'unknown';
  }

  // ============================================
  // Transformateurs pour formater les données comme l'API
  // ============================================

  private transformContrat(item: any): any {
    return {
      ...item,
      codeemp: item.codeempData || item.codeemp,
      matricule: item.matriculeData || item.matricule,
      userCreation: item.userCreation || null
    };
  }

  private transformPaiement(item: any): any {
    return {
      ...item,
      contrat_id: item.contrat_idData || item.contrat_id,
      modePaiement: item.modePaiementData || item.modePaiement,
      userCreation: item.userCreation || null,
      userAgenceCreate: item.userAgenceCreate || null,
      userRegularisation: item.userRegularisation || null,
      userAnnulation: item.userAnnulation || null,
      medias: item.medias || []
    };
  }

  private transformPaiementRecette(item: any): any {
    return {
      ...item,
      contrat: item.contrat || item.contrat_idData || null,
      typeRecette: item.typeRecetteData || item.typeRecette,
      mode: item.modeData || item.mode,
      userCreation: item.userCreation || null,
      userAgenceCreate: item.userAgenceCreate || null,
      userAnnulation: item.userAnnulation || null
    };
  }

  private transformArriereElectronic(item: any): any {
    return {
      ...item,
      contrat_id: item.contrat_idData || item.contrat_id,
      userCreation: item.userCreation || null
    };
  }

  private transformUsager(item: any): any {
    return {
      ...item,
      contrats: item.contrats || []
    };
  }

  private transformEmplacement(item: any): any {
    return {
      ...item,
      secteur: item.secteurData || item.secteur,
      contrats: item.contrats || []
    };
  }

  private transformUser(item: any): any {
    return {
      ...item,
      role: item.roleData || item.role,
      userCreation: item.userCreation || null
    };
  }

  private transformRole(item: any): any {
    return {
      ...item,
      rolePermissions: item.rolePermissions || [],
      users: item.users || []
    };
  }

  private transformPermission(item: any): any {
    return {
      ...item,
      rolePermissions: item.rolePermissions || [],
      userCreation: item.userCreation || null
    };
  }

  private transformRolePermission(item: any): any {
    return {
      ...item,
      role: item.role || null,
      permission: item.permission || null
    };
  }

  private transformModePaiement(item: any): any {
    return {
      ...item,
      userCreation: item.userCreation || null
    };
  }

  private transformSecteur(item: any): any {
    return {
      ...item,
      zone: item.zone || null,
      userCreation: item.userCreation || null
    };
  }

  private transformZone(item: any): any {
    return {
      ...item,
      marche: item.marche || null,
      userCreation: item.userCreation || null
    };
  }

  private transformMarche(item: any): any {
    return {
      ...item,
      userCreation: item.userCreation || null
    };
  }

  private transformActivite(item: any): any {
    return {
      ...item,
      categorie: item.categorie || null,
      userCreation: item.userCreation || null
    };
  }
}

// Instance singleton
export const offlineApiService = new OfflineApiService();

